import { useLanguage } from '../contexts/LanguageContext';

// Translation key mappings for the application
const translations = {
  en: {
    // Navigation
    'nav.dinnerPlanning': 'Dinner Planning',
    'nav.dishLibrary': 'Dish Library',
    'nav.guestManagement': 'Guest Management',
    'nav.aiAssistant': 'AI Assistant',
    'nav.home': 'Home',
    'nav.dashboard': 'Dashboard',
    'nav.guests': 'Guests',
    'nav.dishes': 'Dishes',
    'nav.planEvent': 'Plan Event',
    
    // Common actions
    'actions.save': 'Save',
    'actions.cancel': 'Cancel',
    'actions.delete': 'Delete',
    'actions.edit': 'Edit',
    'actions.add': 'Add',
    'actions.create': 'Create',
    'actions.update': 'Update',
    'actions.close': 'Close',
    'actions.next': 'Next',
    'actions.previous': 'Previous',
    'actions.search': 'Search',
    'actions.filter': 'Filter',
    'actions.export': 'Export',
    'actions.import': 'Import',
    'actions.copy': 'Copy',
    'actions.share': 'Share',
    
    // Dinner Planning
    'dinner.title': 'Plan Your Dinner Event',
    'dinner.subtitle': 'Create memorable dinner experiences with intelligent planning and gift coordination',
    'dinner.eventDetails': 'Event Details',
    'dinner.selectGuests': 'Select Guests',
    'dinner.dietarySummary': 'Dietary Summary',
    'dinner.calendarSync': 'Calendar Sync',
    'dinner.advanced': 'Advanced',
    'dinner.quickPlan': 'Quick Plan',
    'dinner.collectFeedback': 'Collect Feedback',
    'dinner.saveEvent': 'Save Event',
    'dinner.eventSummary': 'Event Summary',
    'dinner.eventDate': 'Event Date',
    'dinner.guests': 'Guests',
    'dinner.duration': 'Duration',
    'dinner.location': 'Location',
    'dinner.readyToSave': 'Ready to save',
    'dinner.completeRequired': 'Complete required fields',
    
    // Guest Management
    'guests.title': 'Guest Management',
    'guests.addGuest': 'Add New Guest',
    'guests.createGuest': 'Create New Guest',
    'guests.editGuest': 'Edit Guest',
    'guests.guestHistory': 'Guest History',
    'guests.familyGroups': 'Family Groups',
    'guests.searchGuests': 'Search guests...',
    'guests.noResults': 'No guests found',
    'guests.totalGuests': 'Total Guests',
    'guests.activeGuests': 'Active Guests',
    'guests.recentlyAdded': 'Recently Added',
    
    // Dish Library
    'dishes.title': 'Dish Library',
    'dishes.addDish': 'Add New Dish',
    'dishes.createDish': 'Create New Dish',
    'dishes.editDish': 'Edit Dish',
    'dishes.searchDishes': 'Search dishes...',
    'dishes.category': 'Category',
    'dishes.cuisine': 'Cuisine',
    'dishes.difficulty': 'Difficulty',
    'dishes.prepTime': 'Prep Time',
    'dishes.cookTime': 'Cook Time',
    'dishes.servings': 'Servings',
    'dishes.ingredients': 'Ingredients',
    'dishes.instructions': 'Instructions',
    'dishes.notes': 'Notes',
    
    // Forms
    'form.required': 'This field is required',
    'form.invalidEmail': 'Please enter a valid email',
    'form.invalidPhone': 'Please enter a valid phone number',
    'form.passwordTooShort': 'Password must be at least 8 characters',
    'form.passwordsNotMatch': 'Passwords do not match',
    'form.selectOption': 'Please select an option',
    'form.enterValue': 'Please enter a value',
    
    // Status messages
    'status.success': 'Success',
    'status.error': 'Error',
    'status.warning': 'Warning',
    'status.info': 'Information',
    'status.loading': 'Loading...',
    'status.saving': 'Saving...',
    'status.saved': 'Saved successfully',
    'status.deleted': 'Deleted successfully',
    'status.updated': 'Updated successfully',
    'status.created': 'Created successfully',
    
    // Time and dates
    'time.today': 'Today',
    'time.yesterday': 'Yesterday',
    'time.tomorrow': 'Tomorrow',
    'time.thisWeek': 'This week',
    'time.lastWeek': 'Last week',
    'time.nextWeek': 'Next week',
    'time.thisMonth': 'This month',
    'time.lastMonth': 'Last month',
    'time.nextMonth': 'Next month',
    'time.minutes': 'minutes',
    'time.hours': 'hours',
    'time.days': 'days',
    'time.weeks': 'weeks',
    'time.months': 'months',
    
    // Account and settings
    'account.settings': 'Account Settings',
    'account.profile': 'Profile',
    'account.preferences': 'Preferences',
    'account.notifications': 'Notifications',
    'account.privacy': 'Privacy',
    'account.help': 'Help & Support',
    'account.signOut': 'Sign Out',
    'account.language': 'Language',
    'account.theme': 'Theme',
    
    // AI Assistant
    'ai.title': 'AI Assistant',
    'ai.chatPlaceholder': 'Ask me anything about cooking, recipes, or dinner planning...',
    'ai.suggestions': 'Quick Suggestions',
    'ai.recipeSuggestion': 'Recipe Suggestion',
    'ai.menuPlanning': 'Menu Planning',
    'ai.dietaryAdvice': 'Dietary Advice',
    'ai.cookingTips': 'Cooking Tips',
  },
  
  es: {
    // Navigation
    'nav.dinnerPlanning': 'Planificación de Cenas',
    'nav.dishLibrary': 'Biblioteca de Platos',
    'nav.guestManagement': 'Gestión de Invitados',
    'nav.aiAssistant': 'Asistente IA',
    'nav.home': 'Inicio',
    'nav.dashboard': 'Panel',
    'nav.guests': 'Invitados',
    'nav.dishes': 'Platos',
    'nav.planEvent': 'Planificar Evento',
    
    // Common actions
    'actions.save': 'Guardar',
    'actions.cancel': 'Cancelar',
    'actions.delete': 'Eliminar',
    'actions.edit': 'Editar',
    'actions.add': 'Añadir',
    'actions.create': 'Crear',
    'actions.update': 'Actualizar',
    'actions.close': 'Cerrar',
    'actions.next': 'Siguiente',
    'actions.previous': 'Anterior',
    'actions.search': 'Buscar',
    'actions.filter': 'Filtrar',
    'actions.export': 'Exportar',
    'actions.import': 'Importar',
    'actions.copy': 'Copiar',
    'actions.share': 'Compartir',
    
    // Dinner Planning
    'dinner.title': 'Planifica tu Evento de Cena',
    'dinner.subtitle': 'Crea experiencias culinarias memorables con planificación inteligente y coordinación de regalos',
    'dinner.eventDetails': 'Detalles del Evento',
    'dinner.selectGuests': 'Seleccionar Invitados',
    'dinner.dietarySummary': 'Resumen Dietético',
    'dinner.calendarSync': 'Sincronización de Calendario',
    'dinner.advanced': 'Avanzado',
    'dinner.quickPlan': 'Planificación Rápida',
    'dinner.collectFeedback': 'Recopilar Comentarios',
    'dinner.saveEvent': 'Guardar Evento',
    'dinner.eventSummary': 'Resumen del Evento',
    'dinner.eventDate': 'Fecha del Evento',
    'dinner.guests': 'Invitados',
    'dinner.duration': 'Duración',
    'dinner.location': 'Ubicación',
    'dinner.readyToSave': 'Listo para guardar',
    'dinner.completeRequired': 'Completar campos requeridos',
    
    // Status messages
    'status.success': 'Éxito',
    'status.error': 'Error',
    'status.warning': 'Advertencia',
    'status.info': 'Información',
    'status.loading': 'Cargando...',
    'status.saving': 'Guardando...',
    'status.saved': 'Guardado exitosamente',
    'status.deleted': 'Eliminado exitosamente',
    'status.updated': 'Actualizado exitosamente',
    'status.created': 'Creado exitosamente',
  },
  
  fr: {
    // Navigation
    'nav.dinnerPlanning': 'Planification de Dîners',
    'nav.dishLibrary': 'Bibliothèque de Plats',
    'nav.guestManagement': 'Gestion des Invités',
    'nav.aiAssistant': 'Assistant IA',
    'nav.home': 'Accueil',
    'nav.dashboard': 'Tableau de Bord',
    'nav.guests': 'Invités',
    'nav.dishes': 'Plats',
    'nav.planEvent': 'Planifier Événement',
    
    // Common actions
    'actions.save': 'Enregistrer',
    'actions.cancel': 'Annuler',
    'actions.delete': 'Supprimer',
    'actions.edit': 'Modifier',
    'actions.add': 'Ajouter',
    'actions.create': 'Créer',
    'actions.update': 'Mettre à jour',
    'actions.close': 'Fermer',
    'actions.next': 'Suivant',
    'actions.previous': 'Précédent',
    'actions.search': 'Rechercher',
    'actions.filter': 'Filtrer',
    'actions.export': 'Exporter',
    'actions.import': 'Importer',
    'actions.copy': 'Copier',
    'actions.share': 'Partager',
    
    // Dinner Planning
    'dinner.title': 'Planifiez votre Événement Dîner',
    'dinner.subtitle': 'Créez des expériences culinaires mémorables avec une planification intelligente et une coordination des cadeaux',
    'dinner.eventDetails': 'Détails de l\'Événement',
    'dinner.selectGuests': 'Sélectionner les Invités',
    'dinner.dietarySummary': 'Résumé Diététique',
    'dinner.calendarSync': 'Synchronisation Calendrier',
    'dinner.advanced': 'Avancé',
    'dinner.quickPlan': 'Planification Rapide',
    'dinner.collectFeedback': 'Collecter les Commentaires',
    'dinner.saveEvent': 'Enregistrer l\'Événement',
    'dinner.eventSummary': 'Résumé de l\'Événement',
    'dinner.eventDate': 'Date de l\'Événement',
    'dinner.guests': 'Invités',
    'dinner.duration': 'Durée',
    'dinner.location': 'Lieu',
    'dinner.readyToSave': 'Prêt à enregistrer',
    'dinner.completeRequired': 'Compléter les champs requis',
    
    // Status messages
    'status.success': 'Succès',
    'status.error': 'Erreur',
    'status.warning': 'Avertissement',
    'status.info': 'Information',
    'status.loading': 'Chargement...',
    'status.saving': 'Enregistrement...',
    'status.saved': 'Enregistré avec succès',
    'status.deleted': 'Supprimé avec succès',
    'status.updated': 'Mis à jour avec succès',
    'status.created': 'Créé avec succès',
  },
  
  de: {
    // Navigation
    'nav.dinnerPlanning': 'Dinnerplanung',
    'nav.dishLibrary': 'Gerichte-Bibliothek',
    'nav.guestManagement': 'Gästeverwaltung',
    'nav.aiAssistant': 'KI-Assistent',
    'nav.home': 'Startseite',
    'nav.dashboard': 'Dashboard',
    'nav.guests': 'Gäste',
    'nav.dishes': 'Gerichte',
    'nav.planEvent': 'Event Planen',
    
    // Common actions
    'actions.save': 'Speichern',
    'actions.cancel': 'Abbrechen',
    'actions.delete': 'Löschen',
    'actions.edit': 'Bearbeiten',
    'actions.add': 'Hinzufügen',
    'actions.create': 'Erstellen',
    'actions.update': 'Aktualisieren',
    'actions.close': 'Schließen',
    'actions.next': 'Weiter',
    'actions.previous': 'Zurück',
    'actions.search': 'Suchen',
    'actions.filter': 'Filtern',
    'actions.export': 'Exportieren',
    'actions.import': 'Importieren',
    'actions.copy': 'Kopieren',
    'actions.share': 'Teilen',
    
    // Dinner Planning
    'dinner.title': 'Planen Sie Ihr Dinner-Event',
    'dinner.subtitle': 'Schaffen Sie unvergessliche kulinarische Erlebnisse mit intelligenter Planung und Geschenkkoordination',
    'dinner.eventDetails': 'Event-Details',
    'dinner.selectGuests': 'Gäste Auswählen',
    'dinner.dietarySummary': 'Ernährungsübersicht',
    'dinner.calendarSync': 'Kalender-Sync',
    'dinner.advanced': 'Erweitert',
    'dinner.quickPlan': 'Schnellplanung',
    'dinner.collectFeedback': 'Feedback Sammeln',
    'dinner.saveEvent': 'Event Speichern',
    'dinner.eventSummary': 'Event-Zusammenfassung',
    'dinner.eventDate': 'Event-Datum',
    'dinner.guests': 'Gäste',
    'dinner.duration': 'Dauer',
    'dinner.location': 'Ort',
    'dinner.readyToSave': 'Bereit zum Speichern',
    'dinner.completeRequired': 'Pflichtfelder ausfüllen',
    
    // Status messages
    'status.success': 'Erfolg',
    'status.error': 'Fehler',
    'status.warning': 'Warnung',
    'status.info': 'Information',
    'status.loading': 'Wird geladen...',
    'status.saving': 'Wird gespeichert...',
    'status.saved': 'Erfolgreich gespeichert',
    'status.deleted': 'Erfolgreich gelöscht',
    'status.updated': 'Erfolgreich aktualisiert',
    'status.created': 'Erfolgreich erstellt',
  },

  it: {
    // Navigation
    'nav.dinnerPlanning': 'Pianificazione Cene',
    'nav.dishLibrary': 'Biblioteca Piatti',
    'nav.guestManagement': 'Gestione Ospiti',
    'nav.aiAssistant': 'Assistente IA',
    'nav.home': 'Home',
    'nav.dashboard': 'Dashboard',
    'nav.guests': 'Ospiti',
    'nav.dishes': 'Piatti',
    'nav.planEvent': 'Pianifica Evento',
    
    // Common actions
    'actions.save': 'Salva',
    'actions.cancel': 'Annulla',
    'actions.delete': 'Elimina',
    'actions.edit': 'Modifica',
    'actions.add': 'Aggiungi',
    'actions.create': 'Crea',
    'actions.update': 'Aggiorna',
    'actions.close': 'Chiudi',
    'actions.next': 'Avanti',
    'actions.previous': 'Indietro',
    'actions.search': 'Cerca',
    'actions.filter': 'Filtra',
    'actions.export': 'Esporta',
    'actions.import': 'Importa',
    'actions.copy': 'Copia',
    'actions.share': 'Condividi',
    
    // Status messages
    'status.success': 'Successo',
    'status.error': 'Errore',
    'status.warning': 'Avvertimento',
    'status.info': 'Informazione',
    'status.loading': 'Caricamento...',
    'status.saving': 'Salvataggio...',
    'status.saved': 'Salvato con successo',
    'status.deleted': 'Eliminato con successo',
    'status.updated': 'Aggiornato con successo',
    'status.created': 'Creato con successo',
  }
};

export const useTranslation = () => {
  const { currentLanguage } = useLanguage();

  const t = (key, fallback = key) => {
    const languageTranslations = translations?.[currentLanguage] || translations?.en;
    return languageTranslations?.[key] || translations?.en?.[key] || fallback;
  };

  const formatMessage = (key, values = {}) => {
    let message = t(key);
    
    Object.keys(values)?.forEach(placeholder => {
      const regex = new RegExp(`{${placeholder}}`, 'g');
      message = message?.replace(regex, values?.[placeholder]);
    });
    
    return message;
  };

  return {
    t,
    formatMessage,
    currentLanguage,
    isRTL: currentLanguage === 'ar', // Right-to-left languages
  };
};

export default useTranslation;